import * as React from 'react';
import * as moment from 'moment-timezone';
import {useMemo} from 'react';
import {GridColDef, GridRenderCellParams} from '@mui/x-data-grid';
import {Warehouse} from '../../../../shared/CommonInterfaces';
import {ItemLinkCell} from '../../../../shared/mui/flexe-data-grid/ItemLinkCell';
import {InboundState} from '../../InboundInterfaces';
import StatusCell from './StatusCell';

// Defines the shape of the data for each row in the Inbounds grid
export interface InboundsGridContent {
  id: number;
  poNumbers: string[];
  status: string;
  trailerNumber: string;
  vendor: string;
  sealNumber: string;
  estimatedDelivery: string;
  reservation: {id: number; shipper: string};
}

/**
 * Generates column definitions for the Inbounds DataGrid.
 *
 * @param baseUrl - Base URL for constructing links (e.g., '/wh/inbounds').
 * @param currentWarehouse - The currently selected warehouse, if needed for context (e.g., timezone for dates).
 */
export const InboundsGridColumnDefinition = (
  baseUrl: string,
  currentWarehouse: Warehouse
): GridColDef<InboundsGridContent>[] => {
  // flex totals must add up to 1, dynamic widths are not supported in the MUI's free version
  const columnFlexWidths = new Map<string, number>();
  columnFlexWidths.set('id', 0.1);
  columnFlexWidths.set('status', 0.1);
  columnFlexWidths.set('trailerNumber', 0.1);
  columnFlexWidths.set('vendor', 0.1);
  columnFlexWidths.set('sealNumber', 0.1);
  columnFlexWidths.set('po', 0.2);
  columnFlexWidths.set('estimatedDelivery', 0.1);
  columnFlexWidths.set('reservation', 0.2);

  /**
   * Converts a time string from "hh:mm AM/PM" format to "ham/hpm" format.
   * Example: "09:00 AM" -> "9am", "05:30 PM" -> "5:30pm"
   */
  const formatDeliveryWindowDateTime = (timeString: string): string => {
    const m = moment(timeString, 'h:mm A', true); // Strict parsing
    if (!m.isValid()) {
      // Handles empty strings, null/undefined (if passed, though type is string), or malformed strings.
      return timeString;
    }
    return m.minutes() === 0 ? m.format('ha') : m.format('h:mma');
  };

  /**
   * Converts a date string from "YYYY-MM-DD" format to "Month Day" format.
   * Example: "2023-06-03" -> "June 3, 2023", "2023-10-31" -> "October 31, 20223"
   */
  const formatDateToMonthDay = (dateString: string | null | undefined): string => {
    const mDate = moment(dateString, 'YYYY-MM-DD', true); // Strict parsing
    if (mDate.isValid()) {
      return mDate.format('MMMM D, Y');
    } else {
      return dateString; // Return original string if parsing fails
    }
  };

  return useMemo<GridColDef<InboundsGridContent>[]>(
    () => [
      {
        field: 'id',
        headerName: 'Inbound ID',
        flex: columnFlexWidths.get('id'),
        renderCell: (params: GridRenderCellParams<InboundsGridContent>) => {
          return (
            <ItemLinkCell
              baseUrl={`${baseUrl}`}
              item={{
                id: params.row.id, // Assuming id is suitable for URL path
                value: params.row.id.toString() // The text to display for the link
              }}
            />
          );
        },
        sortable: false
      },
      {
        field: 'estimatedDelivery',
        headerName: 'Est. Delivery',
        flex: columnFlexWidths.get('estimatedDelivery'),
        renderCell: (params: GridRenderCellParams<InboundsGridContent>) => {
          if (!params.value) {
            return null; // Or some placeholder like '--'
          }
          return <span>{params.value}</span>;
        },
        // use value getter to turn the object into a readable string for exporting
        valueGetter: (params: GridRenderCellParams<InboundsGridContent>) => {
          return formatDateToMonthDay(params.value);
        },
        sortable: false
      },
      {
        field: 'poNumbers',
        headerName: 'P.O./Customer IDs',
        flex: columnFlexWidths.get('po'),
        renderCell: (params: GridRenderCellParams<InboundsGridContent>) => {
          return (
            <>
              {params.value.map((po, index) => (
                <React.Fragment key={index}>
                  {po}
                  {index < params.value.length - 1 && <br />}
                </React.Fragment>
              ))}
            </>
          );
        },
        sortable: false
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: columnFlexWidths.get('status'),
        renderCell: (params: GridRenderCellParams<InboundsGridContent>) => {
          return <StatusCell status={params.value as InboundState} />;
        },
        sortable: false
      },
      {
        field: 'trailerNumber',
        headerName: 'Trailer Number',
        flex: columnFlexWidths.get('trailerNumber'),
        sortable: false,
        renderCell: (params: GridRenderCellParams<InboundsGridContent>) => {
          if (params.value) {
            return <span>{`${params.value}`}</span>;
          } else {
            return '-';
          }
        }
      },
      {
        field: 'vendor',
        headerName: 'Vendor Id',
        flex: columnFlexWidths.get('vendor'),
        sortable: false,
        renderCell: (params: GridRenderCellParams<InboundsGridContent>) => {
          if (params.value) {
            return <span>{`${params.value}`}</span>;
          } else {
            return '-';
          }
        }
      },
      {
        field: 'sealNumber',
        headerName: 'Seal Number',
        flex: columnFlexWidths.get('sealNumber'),
        sortable: false,
        renderCell: (params: GridRenderCellParams<InboundsGridContent>) => {
          if (params.value) {
            return <span>{`${params.value}`}</span>;
          } else {
            return '-';
          }
        }
      },
      {
        field: 'reservation',
        headerName: 'Reservation',
        flex: columnFlexWidths.get('reservation'),
        renderCell: (params: GridRenderCellParams<InboundsGridContent>) => {
          if (params.value) {
            return <span>{`${params.value}`}</span>;
          } else {
            return null;
          }
        },
        valueGetter: (params: GridRenderCellParams<InboundsGridContent>) => {
          return `${params.value.id}: ${params.value.shipper}`;
        },
        sortable: false
      }
    ],
    [baseUrl, currentWarehouse]
  );
};
